"use client"
import { ArrowRight } from "lucide-react"
import React, { useEffect, useState } from "react"
// Defer loading Lottie to avoid blocking initial animations

import { motion } from "motion/react"
import RotatingText from "@/components/RotatingText.jsx"

export function HeroSection() {
  const [showLottie, setShowLottie] = useState(false)
  const [LottieComp, setLottieComp] = useState<null | ((props: any) => JSX.Element)>(null)

  useEffect(() => {
    const run = async () => {
      setShowLottie(true)
      try {
        const mod = await import("@lottiefiles/dotlottie-react")
        setLottieComp(() => mod.DotLottieReact as unknown as (props: any) => JSX.Element)
      } catch {
        // ignore load failures
      }
    }
    // Mount after idle or short timeout to avoid blocking first paint
    // @ts-ignore
    const ric = typeof requestIdleCallback !== "undefined" ? requestIdleCallback(run) : setTimeout(run, 150)
    return () => {
      // @ts-ignore
      if (typeof cancelIdleCallback !== "undefined") cancelIdleCallback(ric as any)
      else clearTimeout(ric as any)
    }
  }, [])

  return (
    <section className="bg-[#fcf9f2] pt-38 pb-16 sm:pt-36 sm:pb-20 lg:pt-44 lg:pb-24">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid items-center grid-cols-1 gap-16 lg:grid-cols-2">
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-3 py-1 text-xs sm:text-sm font-medium text-[#245E51] bg-[#A8FF51]/20 rounded-full border border-[#A8FF51]/30">
                Learn Quran & Arabic Online
              </div>

              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight lg:text-6xl xl:text-7xl">
                Master the Sacred{" "}
                <motion.span
                  layout
                  initial={false}
                  transition={{ layout: { type: "spring", stiffness: 400, damping: 30 } }}
                  className="inline-flex items-baseline rounded-lg px-3 py-1 bg-gradient-to-r from-[#245E51] to-[#A8FF51]"
                  aria-hidden="false"
                >
                  <span className="text-white leading-none">

                    <RotatingText
                      texts={['Quran', 'Arabic', 'Islam']}
                      mainClassName="px-2 sm:px-2 md:px-3 text-white overflow-hidden py-0.5 sm:py-1 md:py-1.5 justify-center rounded-lg"
                      staggerFrom={"last"}
                      initial={false}
                      animate={{ y: 0 }}
                      exit={{ y: "-120%" }}
                      staggerDuration={0.015}
                      splitLevelClassName="overflow-hidden pb-0.5 sm:pb-1 md:pb-1"
                      transition={{ type: "spring", damping: 28, stiffness: 380 }}
                      rotationInterval={2600}
                    />
                  </span>
                  
                </motion.span>{" "}
                with Expert Teachers
              </h1>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
                Begin your spiritual journey with personalized Quran and Arabic lessons from certified instructors worldwide.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/free-trial"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-[#245E51] to-[#1a4a3e] rounded-xl hover:from-[#1a4a3e] hover:to-[#245E51] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                role="button"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a
                href="/about"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                role="button"
              >
                Learn More
              </a>
            </div>

          </div>

          <div className="hidden lg:flex relative w-full overflow-hidden">
            <div className="relative w-full aspect-[4/3]">
              {showLottie && LottieComp ? (
                <LottieComp
                  src="https://lottie.host/7b795c4a-181d-4dab-b71d-6db9c66ba740/PYY2O37pZ6.lottie"
                  loop
                  autoplay
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
            {/* Noscript / fallback image */}
            <noscript>
              <img
                src="/quran-with-tajweed-marks.jpg"
                alt="Quran with tajweed marks"
                className="block w-full h-auto object-contain"
              />
            </noscript>
          </div>
        </div>
      </div>
    </section>
  )
}
